import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, User, ArrowLeft, Facebook, Twitter, Linkedin, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { SEOHead } from "@/components/SEOHead";
import BlogSidebar from "@/components/BlogSidebar";

export default function BlogPostRedLightSkin() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="Rejuvenating Your Skin: The Anti-Aging Benefits of Red Light Therapy | Longview, TX"
        description="Discover how Red Light Therapy stimulates collagen production, reduces wrinkles, and improves overall skin health naturally in East Texas."
        ogImage="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/assets/cf0ceb09-514d-424a-bd29-c804020d9622.png&w=1280&output=webp&q=80"
        canonical="https://woodsspine.com/blog/red-light-skin-rejuvenation"
      />
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-12">
        <Button variant="ghost" className="mb-6 hover:bg-transparent pl-0 text-muted-foreground hover:text-foreground" asChild>
          <Link to="/blog">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
          </Link>
        </Button>
        <div className="lg:grid lg:grid-cols-[1fr_350px] gap-12">
          <div className="space-y-8">
            <header className="space-y-4">
              <Badge className="bg-accent text-white hover:bg-accent/90 border-0 shadow-sm">
                Red Light Therapy
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold leading-tight text-primary">
                Rejuvenating Your Skin: The Anti-Aging Benefits of Red Light Therapy
              </h1>
              <div className="flex flex-wrap items-center text-sm text-muted-foreground gap-4">
                <Link to="/about#dr-lance-potter" className="flex items-center hover:text-accent transition-colors">
                  <User className="w-4 h-4 mr-2" />
                  Dr. Lance Potter
                </Link>
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  June 02, 2026
                </span>
                <span>• 5 min read</span>
              </div>
            </header>

            <img 
              src="https://wsrv.nl/?url=https://vibe.filesafe.space/1774569058151009111/assets/cf0ceb09-514d-424a-bd29-c804020d9622.png&w=1280&output=webp&q=80" 
              alt="Woman receiving red light therapy for skin rejuvenation in Longview TX" 
              className="w-full rounded-2xl aspect-video object-cover"
              fetchPriority="high"
              decoding="async"
            />

            <article className="prose prose-lg dark:prose-invert max-w-none">
              <p className="text-xl font-medium text-foreground">
                When it comes to anti-aging and skin health, the market is flooded with expensive creams, harsh chemical peels, and invasive procedures. But what if you could improve your skin's elasticity and tone from the inside out? Red Light Therapy is a scientifically proven, natural way to rejuvenate your skin without any downtime.
              </p>
              <p>
                As we age, our bodies naturally produce less collagen and elastin—the structural proteins that keep our skin firm, plump, and youthful. This decline, combined with sun damage and environmental stress, leads to wrinkles, fine lines, and sagging. While topical creams can only penetrate so far, Red Light Therapy works at the cellular level to reverse these effects.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Stimulating Collagen Production Naturally</h2>
              <p>
                <Link to="/longview-red-light-therapy" className="text-accent hover:underline">Red Light Therapy</Link> uses specific wavelengths of light (typically in the 600-650nm range) that penetrate the outer layer of the skin (the epidermis) and reach deep into the dermis. 
              </p>
              <p>
                When this light is absorbed by the cells, it stimulates the mitochondria to produce more cellular energy (ATP). This surge in energy activates fibroblasts, the cells directly responsible for producing collagen and elastin fibers. By ramping up your body's natural collagen production, Red Light Therapy helps smooth out fine lines, reduce the depth of wrinkles, and restore a youthful, healthy glow to the skin.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Healing Sun Damage and Inflammation</h2>
              <p>
                Beyond anti-aging, Red Light Therapy is incredibly effective at reducing skin inflammation and healing damage. The increased blood circulation brings oxygen and nutrients to the skin's surface, helping to repair sun damage, fade scars, and even out skin tone. It is also highly beneficial for patients dealing with inflammatory skin conditions like rosacea, eczema, and acne, as it calms the immune response and promotes tissue repair.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Clinical Evidence and Credibility</h2>
              <p>
                The dermatological benefits of photobiomodulation are backed by robust clinical evidence. Numerous controlled trials have demonstrated that Red Light Therapy significantly improves skin complexion, increases collagen density, and reduces skin roughness and wrinkle severity. It is widely recognized by dermatologists and estheticians as a safe, FDA-cleared, non-thermal treatment that does not damage the skin surface, making it suitable for all skin types.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Skin Wellness in Longview, TX</h2>
              <p>
                At Woods Spine & Wellness in Longview, TX, we believe that true wellness encompasses how you feel and how you look. Our clinical-grade Dahlia Red Light bed provides full-body exposure, meaning you receive the anti-aging and skin-healing benefits from head to toe. Dr. Lance Potter and Dr. Tucker Woods are proud to offer this advanced, holistic therapy to our patients in East Texas. Discover more about our services on our <Link to="/what-we-treat" className="text-accent hover:underline">What We Treat</Link> page.
              </p>

              <h2 className="text-2xl font-bold text-primary mt-10 mb-4">Frequently Asked Questions</h2>
              
              <div className="space-y-6 mt-6">
                <div className="border-b border-border pb-4">
                  <h3 className="text-lg font-bold text-primary mb-2">Can Red Light Therapy burn my skin like the sun?</h3>
                  <p className="text-muted-foreground">No. Red Light Therapy does not use ultraviolet (UV) rays, which are the rays that cause sunburns and skin damage. It is completely safe, gentle, and will not tan or burn your skin.</p>
                </div>
                <div className="border-b border-border pb-4">
                  <h3 className="text-lg font-bold text-primary mb-2">How long until I see improvements in my wrinkles?</h3>
                  <p className="text-muted-foreground">Building new collagen takes time. While you may notice a healthier "glow" after just a few sessions, significant reductions in fine lines and wrinkles typically become visible after 8 to 12 weeks of consistent treatment.</p>
                </div>
                <div className="pb-2">
                  <h3 className="text-lg font-bold text-primary mb-2">Can I use my normal skincare products with this therapy?</h3>
                  <p className="text-muted-foreground">Yes! In fact, Red Light Therapy can enhance the absorption and effectiveness of your high-quality skincare products by increasing local blood circulation and cellular metabolism.</p>
                </div>
              </div>

            </article>

            {/* Social Sharing */}
            <div className="flex items-center gap-4 pt-8 border-t border-border">
              <span className="font-medium text-muted-foreground">Share this article:</span>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Facebook className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Twitter className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <Linkedin className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="rounded-full hover:text-accent">
                  <LinkIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* CTA Block */}
            <div className="bg-secondary/30 p-8 rounded-xl border border-border mt-8">
              <h3 className="text-2xl font-bold text-primary mb-3">Glow from the inside out</h3>
              <p className="text-muted-foreground mb-6">
                Ready to boost your collagen and rejuvenate your skin naturally? Schedule a Red Light Therapy session today.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button className="bg-accent text-white hover:bg-accent/90" asChild>
                  <Link to="/appointment-scheduling">Get Started</Link>
                </Button>
                <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground" asChild>
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="text-sm text-muted-foreground border-t border-border pt-6 mt-8">
              Disclaimer: The information provided in this article is for educational purposes only and does not constitute medical advice. Please consult with a healthcare provider before starting any new treatment.
            </div>
          </div>

          <BlogSidebar />
        </div>
      </main>
      <Footer />
    </div>
  );
}
